import { isEmpty, noop } from 'lodash'
import { useEffect, useRef, useState } from 'react'
import TableColumnChooser from './TableColumnChooser'
import {
  CustomTableProps,
  FIELD_VALUE_CONSTANTS,
  NAMESPACE,
  TableColumnType,
} from '../../utils'
import Tooltip from '../Tooltip'
import { TableHeaderColumns, TableRows } from './TableColumn'
import Pagination from '../Pagination'

const CustomTable = <T,>({
  tableName = '',
  columns = [],
  data = [],
  actions = [],
  onRowClick = noop,
  onColumnClick = noop,
  onActionClick = noop,
  onSortClick = noop,
  onColumnListUpdate = noop,
  isColumnChooser = false,
  headerValues,
  sort = null,
  disabledKey,
  emptyDataMessage = '',
  showTableColumnOnEmpty = false,
  onFilterChange = noop,
  filterObject,
  children,
  noBackgroundHover,
  dataQaId,
  stickyActions = true,
  maxHeight,
  totalPages,
  totalElements,
  numberOfElements,
  paginationCurrentPage,
  onPageChange,
  onRowsChange,
  rowsPerPage = FIELD_VALUE_CONSTANTS.TWENTY_FIVE,
  minHeight = '',
  tabIndex,
  restoreDefault,
  showRestoreButton = false,
  expandRowProperties = null,
  showSubTotal = false,
  subTotalCols = [],
  subtotalObject = {},
  emptyDataMessageHeight = 'h-[15rem]',
  showActionHeading = true,
  actionHeading = NAMESPACE?.ACTIONS,
}: CustomTableProps<T>) => {
  const [tableCol, setTableCol] = useState<TableColumnType<T>[]>(columns)
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [rowPerPage, setRowPerPage] = useState<number>(
    rowsPerPage || FIELD_VALUE_CONSTANTS.TWENTY_FIVE
  )
  const operatorRef = useRef<
    | {
        [key: string]: {
          label: string
          value: string
        }
      }
    | undefined
  >({})
  const handleOnColUpdate = (colList: TableColumnType<T>[]) => {
    setTableCol([...colList])
    onColumnListUpdate([...colList])
  }

  useEffect(() => {
    setTableCol([...columns])
  }, [columns])

  return (
    <div
      className={`w-full h-full overflow-x-auto ${
        showTableColumnOnEmpty ||
        !isEmpty(data) ||
        Object.keys(filterObject?.search_criteria ?? {}).length !== 0
          ? 'flex flex-col'
          : ''
      } `}
      data-qa-id={dataQaId ?? tableName ?? ''}
    >
      {!showTableColumnOnEmpty &&
      isEmpty(data) &&
      Object.keys(filterObject?.search_criteria ?? {}).length === 0 ? (
        isEmpty(children) ? (
          <div
            className={`${emptyDataMessageHeight} flex justify-center items-center`}
          >
            <span className="font-light text-xs leading-4">
              {emptyDataMessage}
            </span>
          </div>
        ) : (
          children
        )
      ) : (
        <>
          <div id={`table_filter_${tableName}`} />
          <div
            className={`overflow-y-auto min-h-${minHeight} flex-1`}
            style={{ minHeight: minHeight, maxHeight: maxHeight }}
          >
            <table
              className={
                !isEmpty(actions) && stickyActions
                  ? 'w-full text-xs overflow-x-auto sticky-actions'
                  : 'w-full text-xs overflow-x-auto'
              }
            >
              <thead className="bg-secondary-25 sticky top-0 z-10 w-full rounded-t-md leading-4.5 ">
                <tr className="w-full rounded-t-md">
                  {(tableCol || [])?.map((col, index) => (
                    <TableHeaderColumns
                      key={`${tableName}_header_${index}`}
                      tableName={tableName}
                      column={col}
                      onColumnClick={onColumnClick}
                      onSortClick={onSortClick}
                      headerValues={headerValues}
                      sort={sort}
                      onFilterChange={onFilterChange}
                      filterObject={filterObject}
                      isFilterPresent={
                        tableCol?.find((col) => col.filterData) !== undefined
                      }
                      operatorRef={operatorRef}
                      tabIndex={tabIndex}
                    />
                  ))}
                  {!isEmpty(actions) ? (
                    <th
                      className={`px-4 py-2.5 text-center max-w-[16rem] min-w-fit border border-white action-col align-top !bg-secondary-25`}
                    >
                      <span className="block w-full truncate font-normal leading-4.5 text-xs">
                        {showActionHeading ? actionHeading : ''}
                      </span>
                    </th>
                  ) : null}
                  {isColumnChooser ? (
                    <th className="max-w-11 w-11 px-4 py-2.5 border border-white action-col align-top !bg-secondary-25">
                      <Tooltip
                        content="Add column"
                        position="left"
                        className="max-w-28"
                      >
                        <TableColumnChooser
                          currentCol={tableCol}
                          onColumnListUpdate={handleOnColUpdate}
                          restoreDefault={restoreDefault}
                          showRestoreButton={showRestoreButton}
                        />
                      </Tooltip>
                    </th>
                  ) : null}
                </tr>
                {/* {tableCol?.find((col) => col.filterData) ? (
                  <tr className="w-full rounded-t-md">
                    {tableCol?.map(
                      (col, index) =>
                        !col?.isHidden && (
                          <FilterHeader
                            key={`${tableName}_filter_${index}`}
                            columnData={col}
                            tableName={tableName}
                            handleFilterChange={handleFilterChange}
                            filterObject={filterObject}
                          />
                        )
                    )}
                    {!isEmpty(actions) ? (
                      <th className="border border-accent-0 action-col"></th>
                    ) : null}
                    {isColumnChooser ? (
                      <th className="border border-accent-0"></th>
                    ) : null}
                  </tr>
                ) : null} */}
              </thead>
              <tbody className="w-full font-medium ">
                {isEmpty(data) ? (
                  <tr>
                    <td
                      className={`text-center ${emptyDataMessageHeight}`}
                      colSpan={1000}
                    >
                      {isEmpty(children) ? (
                        <span className="font-light text-xs leading-4">
                          {Object.keys(filterObject?.search_criteria ?? {})
                            .length > 0
                            ? NAMESPACE.NO_RECORDS_FOUND
                            : emptyDataMessage}
                        </span>
                      ) : (
                        children
                      )}
                    </td>
                  </tr>
                ) : null}
                {data?.map((row, rowIndex) => (
                  <TableRows
                    key={`${tableName}_row_${rowIndex}`}
                    row={row}
                    tableName={tableName}
                    columns={tableCol || []}
                    rowIndex={rowIndex}
                    actions={
                      actions?.map((action) => ({
                        ...action,
                        tabIndex: tabIndex,
                      })) || []
                    }
                    onRowClick={onRowClick}
                    onColumnClick={onColumnClick}
                    onActionClick={onActionClick}
                    isColumnChooser={isColumnChooser}
                    isDisabled={
                      disabledKey ? Boolean(row?.[disabledKey]) : false
                    }
                    expandRowProperties={expandRowProperties}
                    noBackgroundHover={noBackgroundHover}
                    tabIndex={tabIndex}
                  />
                ))}
              </tbody>
              <tfoot className="sticky bottom-0 z-10  border-t border-secondary-100">
                {showSubTotal && (
                  <tr>
                    {tableCol.map((col, index) => {
                      if (subTotalCols?.includes(col.accessor as string)) {
                        return (
                          <td
                            className="pl-4 py-2.5 text-xs font-medium"
                            key={`${tableName}_subtotal_${index}`}
                          >
                            SubTotal:{' '}
                            <span className="border-secondary-100 border py-2 px-3 rounded">
                              ${' '}
                              {subtotalObject?.[col.accessor as string] ?? '-'}
                            </span>
                          </td>
                        )
                      }
                      return (
                        <td
                          key={`${tableName}_empty_${index}`}
                          // className="py-2.5"
                        >
                          &nbsp;
                        </td>
                      )
                    })}
                  </tr>
                )}
              </tfoot>
            </table>
          </div>
          {data?.length > 0 && onPageChange && (
            <Pagination
              currentPage={paginationCurrentPage ?? currentPage}
              totalPages={totalPages ?? 0}
              totalElements={totalElements ?? 0}
              numberOfElements={numberOfElements ?? 0}
              onPageChange={(
                page: number,
                rPerPage: number,
                pageChangeMethod?: string
              ) => {
                page = rowPerPage !== rPerPage ? 1 : page
                setCurrentPage(page)
                setRowPerPage(rPerPage)
                onPageChange(page, rPerPage, pageChangeMethod)
              }}
              onRowsChange={onRowsChange}
              rowsPer={rowsPerPage}
            />
          )}
        </>
      )}
    </div>
  )
}

export default CustomTable
